import React, { useState, useEffect, useCallback } from 'react';
import { 
  FiBarChart2, 
  FiUsers, 
  FiCalendar, 
  FiDollarSign, 
  FiActivity,
  FiTrendingUp,
  FiDownload,
  FiX,
  FiPrinter,
  FiFilter
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../utils/axiosConfig';
import LoadingSpinner from '../components/LoadingSpinner';
import { useConfiguracoes, buscarConfiguracoesFinanceirasDoBanco } from '../utils/configuracoes';
import { useAuth } from '../contexts/AuthContext';
import styles from './Relatorios.module.css';

// ✅ DADOS ESTÁTICOS REMOVIDOS - Usando apenas dados reais do banco

// Função para filtrar dados por período
const filtrarPorPeriodo = (dados, periodo, campoData = 'data_consulta') => {
  if (!Array.isArray(dados) || dados.length === 0) {
    return [];
  }
  
  // Trabalhar com datas locais, não UTC
  const hoje = new Date();
  const anoAtual = hoje.getFullYear();
  const mesAtual = hoje.getMonth();
  const diaAtual = hoje.getDate();
  
  // Criar data de início baseado no período (horário local)
  let dataInicio;
  
  switch (periodo) {
    case 'diario':
      // Início do dia atual
      dataInicio = new Date(anoAtual, mesAtual, diaAtual, 0, 0, 0, 0);
      break;
    case 'semanal':
      // 7 dias atrás, início do dia
      dataInicio = new Date(anoAtual, mesAtual, diaAtual - 7, 0, 0, 0, 0);
      break;
    case 'mensal':
      // Primeiro dia do mês atual
      dataInicio = new Date(anoAtual, mesAtual, 1, 0, 0, 0, 0);
      break;
    case 'anual':
      // Primeiro dia do ano atual
      dataInicio = new Date(anoAtual, 0, 1, 0, 0, 0, 0);
      break;
    default:
      return dados; // Retorna todos se período não reconhecido
  }
  
  // Final do dia atual
  const dataFim = new Date(anoAtual, mesAtual, diaAtual, 23, 59, 59, 999);
  

  
  return dados.filter(item => {
    // Tentar diferentes campos de data
    const dataItem = item[campoData] || item.data || item.created_at || item.data_venda;
    if (!dataItem) {
      console.log('⚠️ FILTRO - Item sem data:', item);
      return true; // Se não tem data, inclui por segurança
    }
    
    // Converter data do item para objeto Date
    const dataFormatada = new Date(dataItem);
    
    // Verificar se a data é válida
    if (isNaN(dataFormatada.getTime())) {
      console.log('⚠️ FILTRO - Data inválida:', dataItem);
      return true; // Incluir por segurança
    }
    
    const incluir = dataFormatada >= dataInicio && dataFormatada <= dataFim;
    

    
    return incluir;
  });
};

// Função para atualizar dados financeiros baseados nos dados reais
const atualizarDadosFinanceiros = (consultas, farmaciaValue, exames) => {
  return {
    totalMensal: consultas.length * 150 + farmaciaValue + exames.length * 80,
    consultasMes: consultas.length,
    exameMes: exames.length,
    farmaciaVendas: farmaciaValue
  };
};

const Relatorios = () => {
  const { valorConsulta: valorConsultaHook, valorRetorno: valorRetornoHook, valorExame: valorExameHook } = useConfiguracoes();
  const { user, getPermissionScope, isAdmin, hasPermission } = useAuth();
  const [loading, setLoading] = useState(false);
  
  // Valores financeiros do banco
  const [valorConsulta, setValorConsulta] = useState(0.00);
  const [valorRetorno, setValorRetorno] = useState(0.00);
  const [valorExame, setValorExame] = useState(0.00);
  const [selectedPeriod, setSelectedPeriod] = useState('mensal');
  const [tipoRelatorio, setTipoRelatorio] = useState('');
  const [showRelatorioModal, setShowRelatorioModal] = useState(false);
  
  // Estados para dados reais do banco
  const [consultasDoBanco, setConsultasDoBanco] = useState([]);
  const [pacientesDoBanco, setPacientesDoBanco] = useState([]);
  const [medicosDoBanco, setMedicosDoBanco] = useState([]);
  const [vendasFarmacia, setVendasFarmacia] = useState([]);
  const [vendasFarmaciaDetalhadas, setVendasFarmaciaDetalhadas] = useState([]);
  const [faturamentoFarmacia, setFaturamentoFarmacia] = useState(0);

  // Estado para dados calculados do dashboard
  const [dashboardData, setDashboardData] = useState({
    totalPacientes: 0,
    consultasMes: 0,
    faturamentoMensal: 0,
    totalMedicos: 0
  });

  // Função para buscar dados reais do banco
  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      console.log('🔄 Carregando dados reais do banco...');
      console.log('📅 Período selecionado:', selectedPeriod);
      
      // Verificar se há token para chamadas autenticadas
      const token = localStorage.getItem('token');
      console.log('🔑 Token disponível:', !!token);
      
      // Adicionar filtro por usuário se escopo for 'own' e não for admin
      const financeiroScope = getPermissionScope('relatorios', 'view_financeiro');
      const isUserAdmin = isAdmin && isAdmin();
      const userIdParam = (!isUserAdmin && financeiroScope === 'own' && user?.id) ? `?user_id=${user.id}` : '';

      const [consultasRes, pacientesRes, medicosRes, farmaciaRes, farmaciaDetalhadaRes] = await Promise.all([
        api.get(`/medical/relatorios/consultas${userIdParam}`).catch((error) => {
          console.error('❌ Erro ao buscar consultas:', error.response?.data || error.message);
          return { data: [] };
        }),
  api.get(`/medical/relatorios/pacientes${userIdParam}`).catch((error) => {
          console.error('❌ Erro ao buscar pacientes:', error.response?.data || error.message);
          return { data: [] };
        }),
  api.get(`/medical/relatorios/medicos${userIdParam}`).catch((error) => {
          console.error('❌ Erro ao buscar médicos:', error.response?.data || error.message);
          return { data: [] };
        }),
        token ? 
          api.get(`/farmacia/vendas${userIdParam}`).catch((error) => {
            console.error('❌ Erro ao buscar farmácia:', error.response?.data || error.message);
            return { data: [] };
          }) : 
          Promise.resolve({ data: [] }), // Retorna vazio se não há token
        token ? 
          api.get(`/farmacia/vendas/relatorio-detalhado${userIdParam}`).catch((error) => {
            console.error('❌ Erro ao buscar farmácia detalhada:', error.response?.data || error.message);
            return { data: [] };
          }) : 
          Promise.resolve({ data: [] }) // Retorna vazio se não há token
      ]);

      // Dados reais das consultas do banco
      console.log('🔍 DEBUG - Resposta completa consultas:', consultasRes);
      console.log('🔍 DEBUG - Dados consultas:', consultasRes.data);
      
      // Verificar se os dados estão no formato correto
      let dadosConsultasReais = [];
      if (consultasRes.data && consultasRes.data.data && Array.isArray(consultasRes.data.data.consultas)) {
        dadosConsultasReais = consultasRes.data.data.consultas;
      } else if (Array.isArray(consultasRes.data)) {
        dadosConsultasReais = consultasRes.data;
      } else if (consultasRes.data && Array.isArray(consultasRes.data.consultas)) {
        dadosConsultasReais = consultasRes.data.consultas;
      }
      
      console.log('📋 Consultas processadas (antes do filtro):', dadosConsultasReais);
      
      // ✨ APLICAR FILTRO DE PERÍODO NAS CONSULTAS
      const consultasFiltradas = filtrarPorPeriodo(dadosConsultasReais, selectedPeriod, 'data_consulta');
      console.log(`📅 Consultas filtradas por ${selectedPeriod}:`, consultasFiltradas);
      
      setConsultasDoBanco(consultasFiltradas);

      // Dados reais dos pacientes do banco
      console.log('🔍 DEBUG - Resposta completa pacientes:', pacientesRes);
      console.log('🔍 DEBUG - Dados pacientes:', pacientesRes.data);
      
      // Verificar se os dados estão no formato correto
      let dadosPacientesReais = [];
      if (pacientesRes.data && pacientesRes.data.data && Array.isArray(pacientesRes.data.data.pacientes)) {
        dadosPacientesReais = pacientesRes.data.data.pacientes;
      } else if (Array.isArray(pacientesRes.data)) {
        dadosPacientesReais = pacientesRes.data;
      } else if (pacientesRes.data && Array.isArray(pacientesRes.data.pacientes)) {
        dadosPacientesReais = pacientesRes.data.pacientes;
      }
      
      console.log('👥 Pacientes processados:', dadosPacientesReais);
      setPacientesDoBanco(dadosPacientesReais);

      // Dados reais dos médicos do banco
      console.log('🔍 DEBUG - Resposta completa médicos:', medicosRes);
      console.log('🔍 DEBUG - Dados médicos:', medicosRes.data);
      
      // Verificar se os dados estão no formato correto
      let dadosMedicosReais = [];
      if (medicosRes.data && medicosRes.data.data && Array.isArray(medicosRes.data.data.medicos)) {
        dadosMedicosReais = medicosRes.data.data.medicos;
      } else if (Array.isArray(medicosRes.data)) {
        dadosMedicosReais = medicosRes.data;
      } else if (medicosRes.data && Array.isArray(medicosRes.data.medicos)) {
        dadosMedicosReais = medicosRes.data.medicos;
      }
      
      console.log('👨‍⚕️ Médicos processados:', dadosMedicosReais);
      setMedicosDoBanco(dadosMedicosReais);

      // Dados reais da farmácia do banco
      console.log('🔍 DEBUG - Resposta completa farmácia:', farmaciaRes);
      console.log('🔍 DEBUG - Dados farmácia:', farmaciaRes.data);
      console.log('🔍 DEBUG - Tipo da resposta:', typeof farmaciaRes.data);
      console.log('🔍 DEBUG - É array?', Array.isArray(farmaciaRes.data));
      
      // Verificar se os dados estão no formato correto
      let dadosFarmaciaReais = [];
      
      // Verificar todas as possíveis estruturas de resposta
      if (farmaciaRes.data && farmaciaRes.data.data && Array.isArray(farmaciaRes.data.data.vendas)) {
        console.log('🔍 DEBUG - Usando estrutura: farmaciaRes.data.data.vendas');
        dadosFarmaciaReais = farmaciaRes.data.data.vendas;
      } else if (farmaciaRes.data && farmaciaRes.data.data && Array.isArray(farmaciaRes.data.data)) {
        console.log('🔍 DEBUG - Usando estrutura: farmaciaRes.data.data (array direto)');
        dadosFarmaciaReais = farmaciaRes.data.data;
      } else if (Array.isArray(farmaciaRes.data)) {
        console.log('🔍 DEBUG - Usando estrutura: farmaciaRes.data (array direto)');
        dadosFarmaciaReais = farmaciaRes.data;
      } else if (farmaciaRes.data && Array.isArray(farmaciaRes.data.vendas)) {
        console.log('🔍 DEBUG - Usando estrutura: farmaciaRes.data.vendas');
        dadosFarmaciaReais = farmaciaRes.data.vendas;
      } else {
        console.log('🔍 DEBUG - Estrutura não reconhecida, tentando extrair qualquer array:', farmaciaRes.data);
      }
      
      if (!token) {
        console.log('⚠️ Farmácia: Dados não carregados - necessário login');
      }
      
      console.log('💊 Farmácia processada (antes do filtro):', dadosFarmaciaReais);
      
      // ✨ APLICAR FILTRO DE PERÍODO NAS VENDAS DA FARMÁCIA
      const farmaciaFiltrada = filtrarPorPeriodo(dadosFarmaciaReais, selectedPeriod, 'data_venda');
      console.log(`📅 Vendas farmácia filtradas por ${selectedPeriod}:`, farmaciaFiltrada);
      
      setVendasFarmacia(farmaciaFiltrada);

      // Calcular faturamento da farmácia usando dados reais filtrados
      console.log('🔍 DEBUG FARMÁCIA - Dados recebidos (filtrados):', farmaciaFiltrada);
      console.log('🔍 DEBUG FARMÁCIA - Quantidade de vendas filtradas:', farmaciaFiltrada.length);
      
      const faturamentoFarmaciaReal = farmaciaFiltrada.reduce((total, venda, index) => {
        const valorVenda = venda.total || venda.valor || venda.valor_total || 0;
        console.log(`🔍 DEBUG FARMÁCIA - Venda ${index + 1}:`, {
          venda: venda,
          total: venda.total,
          valor: venda.valor,
          valor_total: venda.valor_total,
          valorUsado: valorVenda
        });
        return total + valorVenda;
      }, 0);
      
      console.log('🔍 DEBUG FARMÁCIA - Faturamento calculado:', faturamentoFarmaciaReal);
      setFaturamentoFarmacia(faturamentoFarmaciaReal);

      // Processar dados detalhados da farmácia
      let dadosFarmaciaDetalhados = [];
      
      if (farmaciaDetalhadaRes.data && farmaciaDetalhadaRes.data.success && farmaciaDetalhadaRes.data.data && Array.isArray(farmaciaDetalhadaRes.data.data.vendas)) {
        dadosFarmaciaDetalhados = farmaciaDetalhadaRes.data.data.vendas;
      } else if (farmaciaDetalhadaRes.data && Array.isArray(farmaciaDetalhadaRes.data.vendas)) {
        dadosFarmaciaDetalhados = farmaciaDetalhadaRes.data.vendas;
      } else if (Array.isArray(farmaciaDetalhadaRes.data)) {
        dadosFarmaciaDetalhados = farmaciaDetalhadaRes.data;
      }
      
      // Filtrar dados detalhados por período
      const farmaciaDetalhadaFiltrada = filtrarPorPeriodo(dadosFarmaciaDetalhados, selectedPeriod, 'data_venda');
      setVendasFarmaciaDetalhadas(farmaciaDetalhadaFiltrada);

      // Calcular faturamento total usando configurações reais e dados filtrados
      const faturamentoConsultas = consultasFiltradas.reduce((total, consulta) => {
        switch(consulta.tipo_consulta || consulta.tipo) {
          case 'consulta': return total + valorConsulta;
          case 'retorno': return total + valorRetorno;
          case 'exame': return total + valorExame;
          default: return total + valorConsulta;
        }
      }, 0);

      const faturamentoTotal = faturamentoConsultas + faturamentoFarmaciaReal;

      // Atualizar o estado do dashboard com dados reais
      const dadosReais = {
        totalPacientes: dadosPacientesReais.length,
        consultasMes: consultasFiltradas.length,
        faturamentoMensal: faturamentoTotal,
        totalMedicos: dadosMedicosReais.length
      };

      console.log('🔍 DETALHES DOS CÁLCULOS (COM FILTROS):');
      console.log(`📋 Consultas encontradas (${selectedPeriod}):`, consultasFiltradas.length, consultasFiltradas);
      console.log('👥 Pacientes encontrados:', dadosPacientesReais.length, dadosPacientesReais);
      console.log('👨‍⚕️ Médicos encontrados:', dadosMedicosReais.length, dadosMedicosReais);
      console.log(`💊 Vendas farmácia encontradas (${selectedPeriod}):`, farmaciaFiltrada.length, farmaciaFiltrada);
      console.log('💰 Faturamento consultas calculado:', faturamentoConsultas);
      console.log('💰 Faturamento farmácia calculado:', faturamentoFarmaciaReal);
      console.log('💰 Faturamento total:', faturamentoTotal);

      setDashboardData(dadosReais);
      console.log('📊 Dados REAIS carregados:', dadosReais);
      

      
    } catch (error) {
      console.error('❌ Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados dos relatórios');
    } finally {
      setLoading(false);
    }
  }, [valorConsulta, valorRetorno, valorExame, selectedPeriod]);

  // Buscar valores financeiros do banco
  useEffect(() => {
    const carregarValoresFinanceiros = async () => {
      try {
        const configs = await buscarConfiguracoesFinanceirasDoBanco();
        setValorConsulta(configs.valor_consulta !== undefined ? configs.valor_consulta : 0.00);
        setValorRetorno(configs.valor_retorno !== undefined ? configs.valor_retorno : 0.00);
        setValorExame(configs.valor_exame !== undefined ? configs.valor_exame : 0.00);
      } catch (error) {
        console.error('Erro ao carregar valores financeiros:', error);
        // Manter valores padrão
      }
    };
    
    carregarValoresFinanceiros();
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [valorConsulta, valorRetorno, valorExame, selectedPeriod, fetchDashboardData]); // Reagir a mudanças nas configurações e período

  // Verificar se tem pelo menos uma permissão de relatórios - movido para depois dos hooks
  const hasAnyReportPermission = hasPermission('relatorios:view_pacientes') || 
                                 hasPermission('relatorios:view_consultas') || 
                                 hasPermission('relatorios:view_medicos') || 
                                 hasPermission('relatorios:view_farmacia') || 
                                 hasPermission('relatorios:view_financeiro');
  
  // Verificar permissões
  if (!hasAnyReportPermission) {
    return (
      <div className={styles.noPermission}>
        <h2>Acesso Negado</h2>
        <p>Você não tem permissão para acessar os relatórios.</p>
      </div>
    );
  }

  // Calcular dados para os relatórios usando dados reais do banco
  // eslint-disable-next-line no-unused-vars
  const calcularDadosRelatorios = () => {
    // ✅ USAR APENAS DADOS REAIS DO BANCO - Nenhum dado fixo!
    
    // Processar dados reais dos pacientes do banco
    const dadosPacientes = pacientesDoBanco.map(paciente => ({
      id: paciente.id,
      nome: paciente.nome || `${paciente.primeiro_nome || ''} ${paciente.sobrenome || ''}`.trim(),
      idade: paciente.idade || new Date().getFullYear() - new Date(paciente.data_nascimento).getFullYear(),
      telefone: paciente.telefone || paciente.celular || 'Não informado',
      ultimaConsulta: paciente.ultima_consulta || 'Nunca',
      status: paciente.status || (paciente.ativo ? 'Ativo' : 'Inativo')
    }));

    // Processar dados reais das consultas do banco
    const dadosConsultas = consultasDoBanco.map(consulta => ({
      id: consulta.id,
      data: new Date(consulta.data_consulta || consulta.data).toLocaleDateString('pt-BR'),
      horario: consulta.horario || new Date(consulta.data_consulta || consulta.data).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
      paciente: consulta.paciente_nome || consulta.paciente || `Paciente ID ${consulta.paciente_id}`,
      medico: consulta.medico_nome || consulta.medico || `Médico ID ${consulta.medico_id}`,
      tipo: consulta.tipo_consulta || consulta.tipo || 'consulta',
      status: consulta.status || 'Agendada'
    }));

    // Calcular faturamento baseado nas configurações REAIS
    const faturamentoConsultas = dadosConsultas.reduce((total, consulta) => {
      switch(consulta.tipo) {
        case 'consulta': return total + valorConsulta;
        case 'retorno': return total + valorRetorno;
        case 'exame': return total + valorExame;
        default: return total + valorConsulta;
      }
    }, 0);

    // Usar dados reais da farmácia do banco (com verificação de segurança)
    const farmaciaValue = Array.isArray(vendasFarmacia) ? vendasFarmacia.reduce((total, venda) => {
      return total + (venda.valor || 0);
    }, 0) : 0;
    
    const totalFaturamento = faturamentoConsultas + farmaciaValue;

    const dadosFinanceiro = atualizarDadosFinanceiros(dadosConsultas, farmaciaValue, []);

    // Calcular faturamento por médico baseado nos dados reais do banco (com verificações de segurança)
    const dadosMedicos = Array.isArray(medicosDoBanco) ? medicosDoBanco.map(medico => {
      const consultasMedico = Array.isArray(consultasDoBanco) ? consultasDoBanco.filter(c => 
        c.medico_id === medico.id || c.medico === medico.nome
      ) : [];
      const faturamento = consultasMedico.reduce((total, consulta) => {
        switch(consulta.tipo) {
          case 'consulta': return total + valorConsulta;
          case 'retorno': return total + valorRetorno;
          case 'exame': return total + valorExame;
          default: return total + valorConsulta;
        }
      }, 0);
      
      return {
        nome: medico.nome || `Dr. ${medico.primeiro_nome} ${medico.sobrenome}`,
        especialidade: medico.especialidade || 'Não informado',
        avaliacao: medico.avaliacao || null,
        consultas: consultasMedico.length,
        faturamento
      };
    }) : [];

    const dadosGeral = {
      pacientes: { 
        total: Array.isArray(pacientesDoBanco) && pacientesDoBanco.length > 0 ? pacientesDoBanco.length : dadosPacientes.length, 
        novos: Array.isArray(pacientesDoBanco) && pacientesDoBanco.length > 0 ? pacientesDoBanco.filter(p => {
          const cadastro = new Date(p.data_cadastro || p.created_at);
          const hoje = new Date();
          const diffTime = hoje - cadastro;
          const diffDays = diffTime / (1000 * 60 * 60 * 24);
          return diffDays <= 30; // Novos nos últimos 30 dias
        }).length : 2,
        ativos: Array.isArray(pacientesDoBanco) && pacientesDoBanco.length > 0 ? 
          pacientesDoBanco.filter(p => p.status === 'Ativo' || p.ativo === true).length :
          dadosPacientes.filter(p => p.status === 'Ativo').length
      },
      consultas: { 
        total: Array.isArray(consultasDoBanco) && consultasDoBanco.length > 0 ? consultasDoBanco.length : dadosConsultas.length,
        hoje: Array.isArray(consultasDoBanco) && consultasDoBanco.length > 0 ? consultasDoBanco.filter(c => {
          const consultaData = new Date(c.data_consulta || c.data);
          const hoje = new Date();
          return consultaData.toDateString() === hoje.toDateString();
        }).length : 3,
        pendentes: Array.isArray(consultasDoBanco) && consultasDoBanco.length > 0 ? 
          consultasDoBanco.filter(c => c.status === 'Agendada' || c.status === 'Pendente').length : 3
      },
      financeiro: { total: totalFaturamento, consultas: faturamentoConsultas, farmacia: farmaciaValue },
      medicos: { 
        total: medicosDoBanco.length > 0 ? medicosDoBanco.length : 3, 
        ativos: medicosDoBanco.length > 0 ? 
          medicosDoBanco.filter(m => m.ativo === true || m.status === 'Ativo').length : 3,
        consultasMedia: medicosDoBanco.length > 0 ? 
          (consultasDoBanco.length / medicosDoBanco.length).toFixed(1) : '2.0'
      },
      configuracoes: { valorConsulta, valorRetorno, valorExame }
    };

    return {
      dadosPacientes,
      dadosConsultas,
      dadosMedicos,
      dadosGeral,
      farmaciaValue,
      totalFaturamento,
      faturamentoConsultas,
      dadosFinanceiro,
      vendasFarmacia
    };
  };

  // Função específica para carregar dados detalhados da farmácia
  const carregarDadosDetalhadosFarmacia = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return [];
      
      const response = await api.get('/farmacia/vendas/relatorio-detalhado');
      
      if (response.data && response.data.success && response.data.data && Array.isArray(response.data.data.vendas)) {
        return response.data.data.vendas;
      }
      
      return [];
    } catch (error) {
      console.error('Erro ao carregar dados detalhados da farmácia:', error);
      return [];
    }
  };

  const handleGerarRelatorio = async (tipo) => {
    // Se for relatório da farmácia, garantir que os dados estão carregados
    if (tipo === 'farmacia' && vendasFarmaciaDetalhadas.length === 0) {
      setLoading(true);
      const novosdados = await carregarDadosDetalhadosFarmacia();
      if (novosdados.length > 0) {
        setVendasFarmaciaDetalhadas(novosdados);
      }
      setLoading(false);
    }
    
    setTipoRelatorio(tipo);
    setShowRelatorioModal(true);
  };

  const relatóriosDisponiveis = [
    {
      id: 'pacientes',
      titulo: 'Relatório de Pacientes',
      descricao: 'Análise completa dos pacientes cadastrados',
      icon: FiUsers,
      color: '#3B82F6',
      dados: `${dashboardData.totalPacientes} pacientes`
    },
    {
      id: 'consultas',
      titulo: 'Relatório de Consultas',
      descricao: 'Estatísticas de consultas e atendimentos',
      icon: FiCalendar,
      color: '#10B981',
      dados: `${dashboardData.consultasMes} consultas/mês`
    },
    {
      id: 'financeiro',
      titulo: 'Relatório Financeiro',
      descricao: 'Análise de faturamento e receitas',
      icon: FiDollarSign,
      color: '#F59E0B',
      dados: `R$ ${dashboardData.faturamentoMensal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
    },
    {
      id: 'medicos',
      titulo: 'Relatório de Médicos',
      descricao: 'Performance e estatísticas médicas',
      icon: FiUsers,
      color: '#8B5CF6',
      dados: `${dashboardData.totalMedicos} médicos`
    },
    {
      id: 'farmacia',
      titulo: 'Relatório da Farmácia',
      descricao: 'Vendas e estoque de medicamentos',
      icon: FiActivity,
      color: '#EF4444',
      dados: `R$ ${faturamentoFarmacia.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
    },
    {
      id: 'geral',
      titulo: 'Relatório Geral',
      descricao: 'Visão geral de todas as atividades',
      icon: FiBarChart2,
      color: '#6B7280',
      dados: `Total: ${dashboardData.totalPacientes + dashboardData.consultasMes} registros`
    }
  ];

  if (loading) {
    return <LoadingSpinner />;
  }

  // Verificar se pode ver dados financeiros
  const canViewFinancial = hasPermission('relatorios:view') && getPermissionScope('relatorios:view') !== 'none';
  const isGlobalScope = getPermissionScope('relatorios:view') === 'global';

  return (
    <div className={styles.dashboard}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.title}>
            <FiBarChart2 />
            Relatórios do Sistema
          </h1>
          <p className={styles.subtitle}>
            Análises, estatísticas e relatórios da clínica
            <span style={{ 
              marginLeft: '10px', 
              padding: '2px 8px', 
              background: '#e3f2fd', 
              color: '#1976d2', 
              borderRadius: '12px', 
              fontSize: '12px',
              fontWeight: '500'
            }}>
              📅 {selectedPeriod === 'diario' ? 'Hoje' : selectedPeriod === 'semanal' ? 'Esta Semana' : selectedPeriod === 'mensal' ? 'Este Mês' : 'Este Ano'}
            </span>
          </p>
        </div>
        

        
        {/* Aviso de Login */}
        {!localStorage.getItem('token') && (
          <div style={{
            background: '#fff3cd', 
            border: '1px solid #ffeaa7', 
            padding: '12px', 
            borderRadius: '4px',
            marginBottom: '15px',
            color: '#856404'
          }}>
            <strong>⚠️ Aviso:</strong> Para visualizar dados completos da farmácia, faça <a href="/login" style={{color: '#0066cc'}}>login no sistema</a>.
          </div>
        )}
        
        <div className={styles.headerActions}>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className={styles.periodSelect}
          >
            <option value="diario">Hoje</option>
            <option value="semanal">Esta Semana</option>
            <option value="mensal">Este Mês</option>
            <option value="anual">Este Ano</option>
          </select>
          <button className={styles.filterButton}>
            <FiFilter />
            <span>Filtros</span>
          </button>

        </div>
      </div>

      {/* Grid de Relatórios Disponíveis */}
      <div className={styles.reportsGrid}>
        {relatóriosDisponiveis.map((relatorio) => (
          <div
            key={relatorio.id}
            className={styles.reportCard}
            onClick={() => handleGerarRelatorio(relatorio.id)}
            style={{ '--card-color': relatorio.color }}
          >
            <div className={styles.reportIcon}>
              <relatorio.icon />
            </div>
            <div className={styles.reportContent}>
              <h3 className={styles.reportTitle}>{relatorio.titulo}</h3>
              <p className={styles.reportDescription}>{relatorio.descricao}</p>
              <span className={styles.reportData}>{relatorio.dados}</span>
            </div>
            <div className={styles.reportAction}>
              <FiTrendingUp />
            </div>
          </div>
        ))}
      </div>

      {/* Ações Rápidas */}
      <div className={styles.quickActions}>
        <h2 className={styles.sectionTitle}>Ações Rápidas</h2>
        <div className={styles.quickActionsGrid}>
          <button
            className={styles.quickActionButton}
            onClick={() => handleGerarRelatorio('geral')}
          >
            <FiBarChart2 />
            <span>Relatório Geral</span>
          </button>
          <button
            className={styles.quickActionButton}
            onClick={() => handleGerarRelatorio('financeiro')}
          >
            <FiDollarSign />
            <span>Relatório Financeiro</span>
          </button>
          <button
            className={styles.quickActionButton}
            onClick={() => window.print()}
          >
            <FiDownload />
            <span>Exportar Dados</span>
          </button>
        </div>
      </div>

      {/* Modal de Relatório (implementação básica) */}
      {showRelatorioModal && (
        <RelatorioModal
          tipo={tipoRelatorio}
          onClose={() => setShowRelatorioModal(false)}
          dadosReais={{
            pacientes: pacientesDoBanco,
            consultas: consultasDoBanco,
            medicos: medicosDoBanco,
            vendas: vendasFarmacia,
            vendasDetalhadas: vendasFarmaciaDetalhadas,
            faturamentoFarmacia: faturamentoFarmacia,
            valorConsulta: valorConsulta,
            valorRetorno: valorRetorno,
            valorExame: valorExame
          }}
        />
      )}
    </div>
  );
};

// Modal completo para exibir todos os relatórios
const RelatorioModal = ({ tipo, onClose, dadosReais }) => {
  const hoje = new Date().toLocaleDateString('pt-BR');
  
  // DEBUG: Verificar dados recebidos no modal
  console.log('🔍 DEBUG MODAL - dadosReais completos:', dadosReais);
  console.log('🔍 DEBUG MODAL - pacientes recebidos:', dadosReais?.pacientes);
  
  // Usar dados reais do banco em vez de dados estáticos
  const dadosPacientes = dadosReais?.pacientes || [];
  const dadosConsultas = dadosReais?.consultas || [];
  const dadosMedicos = dadosReais?.medicos || [];
  const vendasFarmacia = dadosReais?.vendas || [];
  const vendasFarmaciaDetalhadas = dadosReais?.vendasDetalhadas || [];
  const farmaciaValue = dadosReais?.faturamentoFarmacia || 0;
  const { valorConsulta, valorRetorno, valorExame } = dadosReais || {};
  

  
  console.log('🔍 DEBUG MODAL - dadosPacientes após processamento:', dadosPacientes);
  
  // Processar dados reais dos pacientes do banco
  const pacientesProcessados = dadosPacientes.map(paciente => ({
    id: paciente.id,
    nome: paciente.nome || `${paciente.primeiro_nome || ''} ${paciente.sobrenome || ''}`.trim() || 'Nome não informado',
    idade: paciente.idade || (paciente.data_nascimento ? 
      new Date().getFullYear() - new Date(paciente.data_nascimento).getFullYear() : 'N/A'),
    telefone: paciente.telefone || paciente.celular || 'Não informado',
    ultimaConsulta: paciente.ultima_consulta || 'Nunca',
    status: paciente.status || (paciente.ativo ? 'Ativo' : 'Inativo')
  }));

  // Processar dados reais das consultas do banco
  const consultasProcessadas = dadosConsultas.map(consulta => ({
    id: consulta.id,
    data: consulta.data_consulta ? new Date(consulta.data_consulta).toLocaleDateString('pt-BR') : 'N/A',
    horario: consulta.data_consulta ? new Date(consulta.data_consulta).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : 'N/A',
    paciente: consulta.paciente_nome || `Paciente ID ${consulta.paciente_id}`,
    medico: consulta.medico_nome || `Médico ID ${consulta.medico_id}`,
    tipo: consulta.tipo_consulta || consulta.tipo || 'consulta',
    status: consulta.status || 'Agendada'
  }));

  // Processar dados reais dos médicos do banco
  const medicosProcessados = dadosMedicos.map(medico => ({
    id: medico.id,
    nome: medico.nome || `${medico.primeiro_nome || ''} ${medico.sobrenome || ''}`.trim() || 'Nome não informado',
    especialidade: medico.especialidade || 'Não informado',
    avaliacao: medico.avaliacao || null,
    consultas: dadosConsultas.filter(c => c.medico_id === medico.id).length,
    faturamento: dadosConsultas.filter(c => c.medico_id === medico.id).reduce((total, consulta) => {
      switch(consulta.tipo_consulta || consulta.tipo) {
        case 'consulta': return total + (valorConsulta || 0);
        case 'retorno': return total + (valorRetorno || 0);
        case 'exame': return total + (valorExame || 0);
        default: return total + (valorConsulta || 0);
      }
    }, 0)
  }));

  const faturamentoConsultas = consultasProcessadas.reduce((total, consulta) => {
    switch(consulta.tipo) {
      case 'consulta': return total + (valorConsulta || 0);
      case 'retorno': return total + (valorRetorno || 0);
      case 'exame': return total + (valorExame || 0);
      default: return total + (valorConsulta || 0);
    }
  }, 0);
  const totalFaturamento = faturamentoConsultas + farmaciaValue;
  const dadosGeral = {
    pacientes: { 
      total: pacientesProcessados.length, 
      novos: pacientesProcessados.filter(p => {
        // Se tiver data de cadastro, calcular se é novo (últimos 30 dias)
        if (dadosPacientes.find(orig => orig.id === p.id)?.created_at) {
          const cadastro = new Date(dadosPacientes.find(orig => orig.id === p.id).created_at);
          const hoje = new Date();
          const diffTime = hoje - cadastro;
          const diffDays = diffTime / (1000 * 60 * 60 * 24);
          return diffDays <= 30;
        }
        return false;
      }).length,
      ativos: pacientesProcessados.filter(p => p.status === 'Ativo').length
    },
    consultas: { 
      total: consultasProcessadas.length,
      hoje: consultasProcessadas.filter(c => {
        const hoje = new Date().toDateString();
        const consultaData = new Date(c.data || '1970-01-01').toDateString();
        return consultaData === hoje;
      }).length,
      pendentes: consultasProcessadas.filter(c => c.status === 'Pendente' || c.status === 'Agendada').length
    },
    medicos: { 
      total: medicosProcessados.length, 
      ativos: medicosProcessados.filter(m => {
        const medicoOriginal = dadosMedicos.find(orig => orig.id === m.id);
        return medicoOriginal?.ativo !== false && medicoOriginal?.status !== 'Inativo';
      }).length,
      consultasMedia: medicosProcessados.length > 0 ? 
        (consultasProcessadas.length / medicosProcessados.length).toFixed(1) : '0.0'
    }
  };

  const renderConteudoRelatorio = () => {
    switch(tipo) {
      case 'pacientes':
        return (
          <div>
            <h3>Relatório de Pacientes - {hoje}</h3>
            <div className={styles.reportStats}>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Total de Pacientes:</span>
                <span className={styles.statValue}>{dadosGeral.pacientes.total}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Pacientes Novos (30 dias):</span>
                <span className={styles.statValue}>{dadosGeral.pacientes.novos}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Pacientes Ativos:</span>
                <span className={styles.statValue}>{dadosGeral.pacientes.ativos}</span>
              </div>
            </div>
            <div className={styles.reportTable}>
              <table>
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Idade</th>
                    <th>Telefone</th>
                    <th>Última Consulta</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(pacientesProcessados) && pacientesProcessados.length > 0 ? pacientesProcessados.map(paciente => (
                    <tr key={paciente.id}>
                      <td>{paciente.nome}</td>
                      <td>{paciente.idade === 'N/A' ? paciente.idade : `${paciente.idade} anos`}</td>
                      <td>{paciente.telefone}</td>
                      <td>{paciente.ultimaConsulta}</td>
                      <td>
                        <span className={`${styles.status} ${styles[paciente.status?.toLowerCase()]}`}>
                          {paciente.status}
                        </span>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                        Nenhum paciente encontrado no banco de dados
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'consultas':
        return (
          <div>
            <h3>Relatório de Consultas - {hoje}</h3>
            <div className={styles.reportStats}>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Total de Consultas:</span>
                <span className={styles.statValue}>{dadosGeral.consultas.total}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Consultas Hoje:</span>
                <span className={styles.statValue}>{dadosGeral.consultas.hoje}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Consultas Pendentes:</span>
                <span className={styles.statValue}>{dadosGeral.consultas.pendentes}</span>
              </div>
            </div>
            <div className={styles.reportTable}>
              <table>
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Horário</th>
                    <th>Paciente</th>
                    <th>Médico</th>
                    <th>Tipo</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(consultasProcessadas) && consultasProcessadas.length > 0 ? consultasProcessadas.map(consulta => (
                    <tr key={consulta.id}>
                      <td>{consulta.data}</td>
                      <td>{consulta.horario}</td>
                      <td>{consulta.paciente}</td>
                      <td>{consulta.medico}</td>
                      <td>{consulta.tipo}</td>
                      <td>
                        <span className={`${styles.status} ${styles[consulta.status?.toLowerCase()]}`}>
                          {consulta.status}
                        </span>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                        Nenhuma consulta encontrada no banco de dados
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'financeiro':
        return (
          <div>
            <h3>Relatório Financeiro - {hoje}</h3>
            <div className={styles.reportStats}>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Faturamento Total:</span>
                <span className={styles.statValue}>R$ {totalFaturamento.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Faturamento Consultas:</span>
                <span className={styles.statValue}>R$ {faturamentoConsultas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Faturamento Farmácia:</span>
                <span className={styles.statValue}>R$ {farmaciaValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Valor por Consulta:</span>
                <span className={styles.statValue}>R$ {Number(valorConsulta).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Valor por Retorno:</span>
                <span className={styles.statValue}>R$ {Number(valorRetorno).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Valor por Exame:</span>
                <span className={styles.statValue}>R$ {Number(valorExame).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>
        );

      case 'medicos':
        return (
          <div>
            <h3>Relatório de Médicos - {hoje}</h3>
            <div className={styles.reportStats}>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Total de Médicos:</span>
                <span className={styles.statValue}>{dadosGeral.medicos.total}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Médicos Ativos:</span>
                <span className={styles.statValue}>{dadosGeral.medicos.ativos}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Média de Consultas:</span>
                <span className={styles.statValue}>{dadosGeral.medicos.consultasMedia}</span>
              </div>
            </div>
            <div className={styles.reportTable}>
              <table>
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Especialidade</th>
                    <th>Avaliação</th>
                    <th>Consultas</th>
                    <th>Faturamento</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(medicosProcessados) && medicosProcessados.length > 0 ? medicosProcessados.map((medico, index) => (
                    <tr key={medico.id || index}>
                      <td>{medico.nome}</td>
                      <td>{medico.especialidade}</td>
                      <td>⭐ {medico.avaliacao !== null ? medico.avaliacao.toFixed(1) : 'N/A'}</td>
                      <td>{medico.consultas}</td>
                      <td>R$ {medico.faturamento.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                        Nenhum médico encontrado no banco de dados
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'farmacia':
        // Se não há dados detalhados, usar dados da API normal como fallback
        const dadosParaTabela = vendasFarmaciaDetalhadas && vendasFarmaciaDetalhadas.length > 0 
          ? vendasFarmaciaDetalhadas 
          : vendasFarmacia;
        
        return (
          <div>
            <h3>Relatório da Farmácia - {hoje}</h3>
            <div className={styles.reportStats}>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Faturamento Farmácia:</span>
                <span className={styles.statValue}>R$ {farmaciaValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Total de Vendas:</span>
                <span className={styles.statValue}>{Array.isArray(dadosParaTabela) ? dadosParaTabela.length : 0}</span>
              </div>
            </div>
            

            
            {/* Tabela de Vendas Detalhadas */}
            <div className={styles.reportTable}>
              <table>
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Medicamento</th>
                    <th>Quantidade</th>
                    <th>Valor Final</th>
                    <th>Vendedor</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(dadosParaTabela) && dadosParaTabela.length > 0 ? 
                    dadosParaTabela.map((venda, index) => (
                      <tr key={venda.id || index}>
                        <td>
                          {venda.data_venda ? 
                            new Date(venda.data_venda).toLocaleDateString('pt-BR') : 
                            'N/A'
                          }
                        </td>
                        <td>{venda.medicamento_nome || 'Medicamento não informado'}</td>
                        <td>{venda.quantidade_ui || venda.quantidade || 1}</td>
                        <td>
                          {(() => {
                            // Garantir que temos um número: pode vir como string ou null
                            const raw = venda.valor_final ?? venda.total ?? venda.valor ?? venda.valor_total ?? 0;
                            const num = Number(raw);
                            const valorExibido = Number.isFinite(num) ? num : 0;
                            return `R$ ${valorExibido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
                          })()}
                        </td>
                        <td>
                          {venda.usuario_criacao || venda.vendedor_nome || 'Usuário não informado'}
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                          {!localStorage.getItem('token') ? 
                            'Faça login para visualizar as vendas da farmácia' :
                            'Nenhuma venda encontrada no período selecionado'
                          }
                        </td>
                      </tr>
                    )
                  }
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'geral':
        return (
          <div>
            <h3>Relatório Geral do Sistema - {hoje}</h3>
            <div className={styles.reportStats}>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Total de Pacientes:</span>
                <span className={styles.statValue}>{dadosGeral.pacientes.total}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Total de Consultas:</span>
                <span className={styles.statValue}>{dadosGeral.consultas.total}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Total de Médicos:</span>
                <span className={styles.statValue}>{dadosGeral.medicos.total}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Faturamento Total:</span>
                <span className={styles.statValue}>R$ {totalFaturamento.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div>
            <h3>Relatório Personalizado - {hoje}</h3>
            <p>Conteúdo do relatório será exibido aqui...</p>
          </div>
        );
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2>Relatório Detalhado</h2>
          <div className={styles.modalActions}>
            <button 
              className={styles.actionButton} 
              onClick={() => window.print()}
              title="Imprimir"
            >
              <FiPrinter />
            </button>
            <button 
              className={styles.actionButton} 
              onClick={() => window.print()}
              title="Baixar PDF"
            >
              <FiDownload />
            </button>
            <button 
              className={styles.closeButton} 
              onClick={onClose}
              title="Fechar"
            >
              <FiX />
            </button>
          </div>
        </div>
        
        <div className={styles.reportContent}>
          {renderConteudoRelatorio()}
        </div>
      </div>
    </div>
  );
};

export default Relatorios;